import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import axios from 'axios';
import {
  getApiInventoryReportLogs,
  getApiInventoryReportLogsDownloads,
  getApiPosReportLogs,
  getApiPosReportLogsDownloads,
  postApiTransmissionsResend,
} from '@/api/api.generated';
import type {
  InventoryReportLog,
  PosReportLog,
  TransmissionDownload as TransmissionDownloadDto,
} from '@/api/types/api.types';
import type {
  Paged,
  TransmissionDownload,
  TransmissionKind,
  TransmissionLog,
  TransmissionOutcome,
  TransmissionQuery,
  TransmissionResendRequest,
} from './types';

type PosParams = NonNullable<Parameters<typeof getApiPosReportLogs>[0]>;
type InventoryParams = NonNullable<
  Parameters<typeof getApiInventoryReportLogs>[0]
>;
type ResendBody = Parameters<typeof postApiTransmissionsResend>[0];

/**
 * Wire names of the transmission integrations, keyed by provider slug.
 *
 * These are a closed enum on the backend (unlike the free-form `provider`
 * query param in the provider registry), so they're typed against the
 * generated params: renaming one in the spec breaks this table at compile
 * time rather than at runtime with a 400.
 *
 * This mapping is also the whole of the per-provider filtering. Each partition
 * key covers exactly one integration, so a Siemens page can only ever request
 * `siemens-*` rows — there's no cross-provider data to filter out client-side.
 */
const INTEGRATIONS: Record<
  string,
  {
    pos: NonNullable<PosParams['integration']>;
    inventory: NonNullable<InventoryParams['integration']>;
  }
> = {
  siemens: {pos: 'siemens-pos', inventory: 'siemens-inventory'},
  'square-d': {pos: 'square-d-pos', inventory: 'square-d-inventory'},
};

/**
 * The integration a provider's stream is stored under, or undefined for a
 * provider that has no transmission log (the document providers).
 */
export function transmissionIntegration(
  providerId: string,
  kind: TransmissionKind
): string | undefined {
  return INTEGRATIONS[providerId]?.[kind];
}

// Documented as 1–100, but the backend only validates that limit is 1–3
// digits: "999" reaches DynamoDB as the page size and "0" removes the limit
// entirely. Clamp here until that's fixed server-side.
const MIN_LIMIT = 1;
const MAX_LIMIT = 100;
const DEFAULT_LIMIT = 25;

function toLimitParam(limit: number | undefined): string {
  const wanted = Math.trunc(limit ?? DEFAULT_LIMIT);
  if (!Number.isFinite(wanted)) return String(DEFAULT_LIMIT);
  return String(Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, wanted)));
}

/**
 * Turn a `YYYY-MM-DD` from a date input into an ISO instant.
 *
 * Both bounds are inclusive on the backend, so a single day is expressed as
 * that day's local midnight through its last millisecond. Interpreting the
 * picked day in the viewer's timezone is what makes "today" mean today.
 */
function toIsoBound(
  day: string | undefined,
  edge: 'start' | 'end'
): string | undefined {
  if (!day) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return undefined;
  const [, y, m, d] = match.map(Number);
  const date =
    edge === 'start'
      ? new Date(y, m - 1, d, 0, 0, 0, 0)
      : new Date(y, m - 1, d, 23, 59, 59, 999);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

const OUTCOMES = new Set<string>(['sent', 'failed', 'pending']);

/**
 * The API normalises `status` into `outcome` already; this only guards against
 * a value the spec doesn't cover reaching the UI. It resolves the same way the
 * backend does — to `pending`, never to `sent`.
 */
function coerceOutcome(raw: unknown): TransmissionOutcome {
  return typeof raw === 'string' && OUTCOMES.has(raw)
    ? (raw as TransmissionOutcome)
    : 'pending';
}

/** Every response is `{data: [...], pagination}`, by-id and downloads included. */
function toList<T>(data: T[] | T | undefined | null): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

function toTransmissionLog(
  row: PosReportLog | InventoryReportLog
): TransmissionLog {
  const base: TransmissionLog = {
    id: row.id,
    integration: row.integration,
    vendorId: row.vendorID,
    status: row.status,
    outcome: coerceOutcome(row.outcome),
    modified: row.modified,
    timestamp: row.timestamp,
    recordCount: row.recordCount,
    batchIndex: row.batchIndex,
    errorMessage: row.errorMessage,
    hasDocument: row.hasDocument,
  };
  // Only POS rows carry a run window; the inventory schema has no such fields.
  if ('runTimestamp' in row) {
    base.runTimestamp = row.runTimestamp;
    base.trigger = row.trigger;
    base.startDate = row.startDate;
    base.endDate = row.endDate;
  }
  return base;
}

function toDownload(dto: TransmissionDownloadDto): TransmissionDownload {
  return {...dto, outcome: coerceOutcome(dto.outcome)};
}

function toListParams(query: TransmissionQuery) {
  return {
    integration: query.integration,
    outcome: query.outcome,
    modifiedFrom: toIsoBound(query.dateFrom, 'start'),
    modifiedTo: toIsoBound(query.dateTo, 'end'),
    limit: toLimitParam(query.limit),
    exclusiveStartKey: query.cursor,
  };
}

/**
 * A page of transmissions for one integration, newest `modified` first.
 *
 * `outcome` is applied as a DynamoDB filter *after* a page is read, so a page
 * can come back far shorter than `limit` while more rows remain. Callers must
 * drive paging off `hasMore`/`nextCursor` only — never off `items.length`.
 */
export function useTransmissionLogs(query: TransmissionQuery) {
  return useQuery({
    queryKey: ['transmissions', query],
    enabled: Boolean(query.integration),
    queryFn: async (): Promise<Paged<TransmissionLog>> => {
      const params = toListParams(query);
      // `integration` is a plain string on the view-model query; the cast is
      // safe because every value comes from the INTEGRATIONS table above.
      const res =
        query.kind === 'pos'
          ? await getApiPosReportLogs(params as PosParams)
          : await getApiInventoryReportLogs(params as InventoryParams);

      return {
        items: toList(res.data).map(toTransmissionLog),
        nextCursor: res.pagination?.lastEvaluatedKey ?? undefined,
        hasMore: Boolean(res.pagination?.hasMore),
      };
    },
  });
}

/**
 * Request a presigned link to one row's archived payload.
 *
 * A mutation rather than a query on purpose: the URL lives five minutes, so
 * every click has to re-request instead of replaying a cached one. The object
 * is HEAD-checked before signing, so a URL that comes back resolves to
 * something that exists.
 */
export function useTransmissionDownload() {
  return useMutation({
    mutationFn: async ({
      kind,
      id,
    }: {
      kind: TransmissionKind;
      id: string;
    }): Promise<TransmissionDownload> => {
      const res =
        kind === 'pos'
          ? await getApiPosReportLogsDownloads({id})
          : await getApiInventoryReportLogsDownloads({id});
      const dto = toList(res.data)[0];
      if (!dto) throw new Error('The download response carried no link.');
      return toDownload(dto);
    },
  });
}

/**
 * Why a download request failed, in words worth showing a user.
 *
 * 409 and 410 are both "no file" but mean different things — nothing was ever
 * archived vs. it aged out — and a user chasing a missing payload needs to
 * know which, so they're never collapsed into one message.
 */
export function describeDownloadError(error: unknown): {
  status?: number;
  message: string;
} {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  switch (status) {
    case 404:
      return {status, message: 'That transmission is no longer on record.'};
    case 409:
      return {
        status,
        message:
          'No document available — this run failed before it archived a payload.',
      };
    case 410:
      return {
        status,
        message:
          'The archived payload has aged out of storage and can no longer be downloaded.',
      };
    case 401:
    case 403:
      return {
        status,
        message: 'You are not permitted to download this payload.',
      };
    default:
      return {
        status,
        message: 'Could not prepare the download. Try again in a moment.',
      };
  }
}

/**
 * Hand the payload to the browser.
 *
 * A new tab keeps the dashboard mounted when S3 serves the object inline; if a
 * popup blocker refuses it (the presigned URL only arrives after an await, so
 * the click gesture may already be spent), navigating this tab still works —
 * an attachment response leaves the page where it is.
 */
export function openDownload(url: string): void {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) window.location.assign(url);
}

// ---------------------------------------------------------------------------
// Resend
//
// Both streams replay, but they don't replay the same way. A POS replay
// re-extracts the documents dated in a window and transmits them again. An
// inventory replay has no window: ingestion overwrites the stock table in
// place, so a run reports the stock as it stands, and a replay reports it as
// it stands *now* — not as it stood when the run being repaired failed.
// ---------------------------------------------------------------------------

/**
 * The integration a provider replays under, or undefined if it has none.
 *
 * Deliberately narrower than `transmissionIntegration`: it returns the closed
 * enum the request body demands rather than a string, so a slug the endpoint
 * doesn't accept can't reach it even by mistake. The return annotation is what
 * makes that a compile error if the two specs ever drift apart.
 */
export function resendIntegration(
  providerId: string,
  kind: TransmissionKind
): ResendBody['integration'] | undefined {
  return INTEGRATIONS[providerId]?.[kind];
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether a `YYYY-MM-DD` names a day that exists — rejects `2026-02-31`. */
function isRealDay(day: string): boolean {
  if (!CALENDAR_DAY.test(day)) return false;
  const parsed = new Date(`${day}T00:00:00.000Z`);
  return (
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day
  );
}

/**
 * Widen a `YYYY-MM-DD` into the inclusive UTC instant bounding that day.
 *
 * UTC on purpose, unlike `toIsoBound` above. That one filters the log on
 * `modified`, a real instant, so reading the picked day in the viewer's
 * timezone is what makes "today" mean today. This one selects *documents*,
 * which the backend stamps at midnight UTC: a local reading would push the
 * start bound hours past a whole day's stamp anywhere west of UTC, and that
 * day would silently replay nothing.
 */
export function toUtcDayBound(
  day: string,
  edge: 'start' | 'end'
): string | undefined {
  if (!isRealDay(day)) return undefined;
  return edge === 'start' ? `${day}T00:00:00.000Z` : `${day}T23:59:59.999Z`;
}

/**
 * The calendar day a POS row's window bound names.
 *
 * `startDate`/`endDate` are documented only as "day", and the two POS flows
 * have been seen writing both a bare `YYYY-MM-DD` and a full ISO instant. Both
 * are UTC stamps, so taking the leading date is right either way.
 */
export function toCalendarDay(value: string | undefined): string | undefined {
  const day = value?.slice(0, 10);
  return day && isRealDay(day) ? day : undefined;
}

/**
 * Build the wire body for a replay, or undefined if the range can't be sent.
 *
 * Rejecting here rather than letting the backend 400 keeps the reason in the
 * form, next to the field that caused it. Note a single day is a valid range:
 * it spans midnight to the last millisecond, so `endDate` is still after
 * `startDate` as the API requires.
 *
 * An inventory body carries the integration alone. The date fields are left
 * off rather than sent empty: the spec rejects a range on an inventory resend
 * instead of ignoring it, so anything in them is a 400.
 */
export function toResendBody(
  request: TransmissionResendRequest
): ResendBody | undefined {
  // Safe because every value comes from `resendIntegration`, whose return type
  // is this same closed enum.
  const integration = request.integration as ResendBody['integration'];
  if (request.kind === 'inventory') return {integration};

  const startDate = toUtcDayBound(request.startDate, 'start');
  const endDate = toUtcDayBound(request.endDate, 'end');
  if (!startDate || !endDate || startDate > endDate) return undefined;
  return {integration, startDate, endDate};
}

/** How many days an inclusive range covers, or undefined if it isn't one. */
export function rangeDayCount(from: string, to: string): number | undefined {
  const start = toUtcDayBound(from, 'start');
  const end = toUtcDayBound(to, 'start');
  if (!start || !end) return undefined;
  // Both bounds are UTC midnight, so this is exact — no DST hour to lose.
  const days = (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1;
  return days >= 1 ? days : undefined;
}

/**
 * Replay an integration's transmission — a POS window, or the stock table.
 *
 * The log is invalidated on success, though the replayed run reports
 * asynchronously — the refetch shows the request was accepted, not that it has
 * landed. Invalidating from the hook rather than a `mutate` callback means it
 * still happens if the dialog is closed while the request is in flight.
 */
export function useResendTransmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: TransmissionResendRequest) => {
      const body = toResendBody(request);
      if (!body) {
        throw new Error(
          'That date range cannot be sent — check the start and end days.'
        );
      }
      return postApiTransmissionsResend(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({queryKey: ['transmissions']});
    },
  });
}

/**
 * Why a replay was refused, in words worth showing a user.
 *
 * A 400 is the one case where the server knows something the form doesn't —
 * a range outside retention, an integration paused — so its own message is
 * preferred over anything generic when it sends one. `kind` only colours the
 * fallback for a 400 that arrives bare: on the POS form the range is the one
 * thing that could be wrong, which is worth saying; an inventory replay has no
 * range to blame.
 */
export function describeResendError(
  error: unknown,
  kind: TransmissionKind = 'pos'
): {
  status?: number;
  message: string;
} {
  if (!axios.isAxiosError(error)) {
    return {
      message:
        error instanceof Error
          ? error.message
          : 'Could not start the resend. Try again in a moment.',
    };
  }

  const status = error.response?.status;
  const body = error.response?.data as
    | {error?: string; message?: string}
    | undefined;
  // The API's own errors carry `error` (the ErrorResponse schema); anything in
  // `message` was written by API Gateway itself, before the Lambda ran.
  const detail = body?.error;
  const gateway = detail ? undefined : body?.message;

  switch (status) {
    case 400:
      return {
        status,
        message:
          detail ??
          (kind === 'pos'
            ? 'The backend rejected that date range.'
            : 'The backend rejected that resend.'),
      };
    case 403:
      // Two very different causes share this status, and the body shape tells
      // them apart: the API answers with `error` (the ErrorResponse schema),
      // API Gateway with `message`. A gateway 403 means the request never
      // reached the API — so it is a deployment or routing fault, not a
      // permissions decision, and saying "not permitted" sends people to audit
      // Cognito groups for a route that isn't there.
      //
      // Don't match on the gateway's wording: an unmatched path answers
      // "Missing Authentication Token" bare, but "Authorization header
      // requires 'Credential'..." once a bearer token is attached, because the
      // gateway tries to read that header as SigV4 before it matches the path.
      // Same cause, two texts — the absence of `error` covers both.
      if (!detail) {
        return {
          status,
          message: `The API never processed this request — API Gateway rejected it at the edge${gateway ? ` ("${gateway.slice(0, 80)}")` : ''}. /api/transmissions/resend is most likely not deployed to this environment.`,
        };
      }
      return {status, message: detail};
    case 401:
      return {
        status,
        message: detail ?? 'You are not permitted to resend transmissions.',
      };
    case 404:
      return {
        status,
        message:
          'That integration is not registered for replay. It may have been renamed.',
      };
    case 500:
      return {
        status,
        message:
          'The resend failed to start. Nothing was transmitted — try again, and check the log before retrying a second time.',
      };
    default:
      return {
        status,
        message: 'Could not start the resend. Try again in a moment.',
      };
  }
}
